// THE COVERAGE GRID — rows, membership, sort and filters. PURE (`now` injected).
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md D17-D20
//
// ⚠️⚠️ THE PAGE ANSWERS ONE QUESTION AND THE GRID IS ITS SHAPE: what needs me
// right now, across every source, for every account. The worklist built in D15
// is NOT replaced by this — it becomes the ROW DETAIL and the SORT KEY, which is
// what its rows were already saying.
//
// The grid absorbs `/reporting/coverage`: status and action in one place.

import type { AccountHealth, SourceHealth, HealthSourceKey } from '../../types/sourceHealth.js';
import { accountStatus, jobStatus, STATUS_RANK, type HealthStatus } from './status.js';
import { yearOnYearRange } from '../portalReport/yearOnYear.js';
import {
  MATRIX_SOURCES,
  MATRIX_SOURCE_LABEL,
  SERVICE_FOR_SOURCE,
  cellStatus,
  enabledFor,
  isCancelled,
  matrixCell,
  type MatrixAccountConfig,
  type MatrixCell,
  type MatrixSourceKey,
} from './matrix.js';

/**
 * ⚠️ WHICH `SourceHealth` FEEDS WHICH COLUMN. Three sources, four columns:
 * demographics is a separate MEASUREMENT of the Meta pull, so it carries its own
 * verdicts under its own key while sharing Meta's expectation and switch.
 */
export const HEALTH_SOURCE_FOR_COLUMN: Record<MatrixSourceKey, HealthSourceKey> = {
  meta: 'meta',
  metaDemographics: 'meta',
  googleAds: 'googleAds',
  crm: 'crm',
};

/** One account, as the grid needs it. */
export interface GridAccount extends MatrixAccountConfig {
  accountType: string;
  /**
   * ⚠️ RESOLVED SERVER-SIDE through `computeBrandAncestorMap` /
   * `computeGroupAncestorMap` — the canonical resolvers every other brand and
   * group surface reads. A row cannot walk its own ancestry (it has no tree),
   * and a second walk here would be a fourth opinion about which brand an
   * account belongs to.
   */
  brandName: string | null;
  groupName: string | null;
  accountManagerName: string | null;
  /**
   * ⚠️ ACTIONABLE ad-account drift only, pre-gated by `isActionableDrift`. Null
   * when there is none. See `matrixCell`.
   */
  drift: {
    kind: string;
    boundAdAccountId: string | null;
    expectedAdAccountIds: string[];
  } | null;
  /**
   * ⚠️ Per source: does the pull actually reach this account, by its OWN link or
   * by SHARED coverage? Supplied by the server from the code that already
   * decides. Never computed here — that would be a fourth opinion about our own
   * system, after `runsMeta`, the link, and the census.
   */
  inRoster: Partial<Record<MatrixSourceKey, boolean>>;
  /** Why it is in the roster, where the answer is non-obvious (shared pull). */
  rosterNote: Partial<Record<MatrixSourceKey, string | null>>;
}

/** One line of the expanded row detail. */
export interface GridRowDetailLine {
  source: MatrixSourceKey;
  cell: MatrixCell;
  /** The account's own dates for this source, where we hold them. */
  dataThrough: string | null;
  coverageFrom: string | null;
  deliveryConfirmedThrough: string | null;
  /**
   * ⚠️ THE PULL UNIT'S floor, not the account's — the coverage witness that
   * separates "not delivered" from "did not advertise". See
   * `yearOnYearForSource`.
   */
  deliveryConfirmedFrom: string | null;
  /**
   * ⚠️⚠️ THE PRIOR-YEAR VERDICT FOR *THIS SOURCE*, and it lives here rather than
   * on the row because that is what it is a property of. A row-level verdict let
   * one source answer for another — see `yearOnYearForSource`.
   */
  yearOnYear: GridYearOnYear | null;
  /**
   * ⚠️ TRUE when this line's data IS old but the verdict was withheld because the
   * SOURCE's pull is behind (§5). The account is not being called healthy — its
   * staleness has one cause, reported once at source level, and this is what
   * lets the finding count what it suppressed.
   */
  stalenessSuppressed: boolean;
  /** Other accounts sharing this account's pull. Empty for a standalone. */
  sharedWith: string[];
  /** What to do about it, or null when there is nothing to say. */
  action: string | null;
  /** ⚠️ Only where a REAL control exists — never a button that does nothing. */
  href: string | null;
}

export interface GridRow {
  account: GridAccount;
  cells: Record<MatrixSourceKey, MatrixCell>;
  /**
   * The worst cell colour on the row, or null when no cell has one.
   * ⚠️ A CANCELLED ROW IS ALWAYS null — its cells can never be `health`, so
   * staleness structurally cannot reach the sort.
   */
  rowStatus: HealthStatus | null;
  /** Cancelled rows: the sources still reaching this account. THE decommission list. */
  liveSources: MatrixSourceKey[];
  /** Enabled-but-unwired sources. The "signed up, never wired" finding. */
  missingSources: MatrixSourceKey[];
  /** ⚠️ A CONFIG fault on this row. Red, and never outranked by staleness. */
  hasDrift: boolean;
  /**
   * ⚠️⚠️ THE PRIOR-YEAR VERDICTS LIVE ON THESE LINES, NOT ON THE ROW. There is
   * deliberately no `row.yearOnYear`: a single one collapsed two sources into
   * one answer, which both MASKED a source's verdict and let the Google causes
   * panel count Meta shortfalls. A consumer that wants Google's verdict must
   * name Google.
   */
  detail: GridRowDetailLine[];
}

/**
 * ⚠️⚠️ ROW MEMBERSHIP, STATED OVER THE THING ITSELF (D17d as corrected by D19c).
 *
 *     a row exists for every account the pull REACHES, plus every account with a
 *     relevant service ticked.
 *
 * NOT by account type — that is a proxy, and it gets two populations wrong in
 * opposite directions: the two brand accounts with REAL Meta ad accounts would
 * be dropped, and operational accounts with nothing wired would be added with
 * nothing to report in them.
 *
 * ⚠️ The second clause is what makes the discrepancy VISIBLE: an account signed
 * up for Meta with no route to it has no roster entry, so a roster-only rule
 * would hide the exact finding this page exists to surface.
 *
 * ⚠️ `reporting` / `content_creation` and the rest are NOT relevant services —
 * only the ones with a column. A row of four "–" is noise, and that is precisely
 * the ~41 flag-only groups D18d found.
 */
export function isGridRow(account: GridAccount): boolean {
  for (const source of MATRIX_SOURCES) {
    if (account.inRoster[source] === true) return true;
    if (enabledFor(source, account).enabled) return true;
  }
  return false;
}

function actionFor(
  source: MatrixSourceKey,
  cell: MatrixCell,
): { action: string | null; href: string | null } {
  // ⚠️ NEVER INVENTS A CAPABILITY. Only Meta has a one-click backfill; CRM and
  // Google Ads are repaired elsewhere, and their lines say WHERE rather than
  // offering a control that does not exist.
  if (cell.kind === 'notEnabled' || cell.kind === 'decommissioned') {
    return { action: null, href: null };
  }
  if (cell.kind === 'stillSyncing') {
    return {
      action:
        source === 'crm'
          ? 'Decommission: turn off this club’s CRM sync.'
          : source === 'googleAds'
            ? 'Decommission: unlink this account from the Google Ads transfer.'
            : 'Decommission: turn off reporting sync and unlink the ad account.',
      href: source === 'crm' ? null : '/reporting/coverage',
    };
  }
  if (cell.kind === 'missing') {
    return {
      action:
        source === 'crm'
          ? 'Check the club id on this account matches a club in the CRM registry.'
          : source === 'googleAds'
            ? 'Check the campaign-name match token resolves this account.'
            : 'Check this account has an ad account and a Zernio profile linked.',
      href: source === 'crm' ? null : '/accounts',
    };
  }
  if (cell.kind === 'unknown') {
    return { action: 'This source could not be read — check its job.', href: null };
  }
  if (cell.kind === 'drift') {
    // ⚠️ A REAL CONTROL: the account page's API Connections section is where the
    // ad account is re-scoped, and the Accounts grid already links there.
    return { action: 'Re-scope this account to the right ad account.', href: '/connection-health' };
  }
  if (cell.status === 'green') return { action: null, href: null };
  const neverLoaded = /never loaded/i.test(cell.reason ?? '');
  if (source === 'crm') {
    return {
      action: neverLoaded
        ? 'Check the club is linked and enabled, then run the CRM sync.'
        : 'Check the CRM sync is reaching this club — see its last run.',
      href: null,
    };
  }
  if (source === 'googleAds') {
    return {
      action: neverLoaded
        ? 'Check the campaign-name match token resolves this account.'
        : 'Check the Google Ads transfer for this account in the Cloud Console.',
      href: null,
    };
  }
  return {
    action: neverLoaded
      ? 'Run a backfill for this account from the Coverage page.'
      : 'Check the account is still connected, then backfill from the Coverage page.',
    href: '/reporting/coverage',
  };
}

/**
 * ⚠️⚠️ WHY A PRIOR YEAR IS MISSING — TWO REASONS, AND ONLY ONE IS ANYBODY'S JOB
 * (D22e).
 *
 *     pending          the pull unit has no facts covering the prior-year window.
 *                      The data never ARRIVED. Re-run that unit's history.
 *     noPriorActivity  the unit DID deliver the window and this account has
 *                      nothing in it. ⚠️ NEVER actionable — the account was not
 *                      advertising, and there is nothing to fetch.
 *
 * ⚠️ Reported identically until D22e, and that mattered: measured 2026-09-22, 58
 * of 171 Google accounts were short and 57 of them were the second kind. 34% of
 * the source flagged amber with nothing anyone could do — "precisely how a
 * worklist becomes wallpaper", by this page's own standard.
 *
 * ⚠️⚠️ IT CARRIES NO `source`, BECAUSE IT NO LONGER TRAVELS ALONE (round 2). A
 * verdict lives ON its `GridRowDetailLine`, which already knows its source — so
 * a second copy here would be a second answer to "which source is this about",
 * and that field is exactly what a mis-scoped consumer read.
 */
export interface GridYearOnYear {
  /** ⚠️ ONLY `pending` is a shortfall. `noPriorActivity` is information. */
  kind: 'pending' | 'noPriorActivity';
  /** The sentence the line shows. */
  message: string;
}

/**
 * ⚠️⚠️ THE SOURCES WHOSE PRIOR-YEAR LINE THIS DESCRIBES. Meta and Google Ads
 * only — the two channels the client report's year-on-year chart draws. CRM has
 * its own comparison (`priorYearCrmRange`) with its own refusal, and
 * demographics is not on that chart at all; giving either a verdict here would
 * describe a chart that does not exist.
 */
export const YEAR_ON_YEAR_SOURCES: readonly MatrixSourceKey[] = ['meta', 'googleAds'];

/**
 * An ISO instant or a plain date -> YYYY-MM-DD. ⚠️ Both shapes reach this
 * function: `coverageFrom` is an instant for Google Ads and a date for Meta, and
 * comparing the two forms as strings is an off-by-one (see below).
 */
const day = (v: string | null | undefined): string | null =>
  v === null || v === undefined ? null : v.slice(0, 10);

/**
 * ⚠️⚠️ THE CONSEQUENCE NOTHING ELSE STATES (D22c). `priorYearUnavailableReason()`
 * withholds the ENTIRE prior-year line if ANY ONE of its twelve months is
 * `no-data` — so an account whose history starts inside that window has NO
 * year-on-year chart on its client report. Not a gap in the line: the whole
 * series, gone. Adding a customer to an MCC silently breaks a client-facing
 * chart, and until this page the only place that was visible was the client's
 * report.
 *
 * ⚠️⚠️ ONE VERDICT PER (ACCOUNT, SOURCE) — NEVER ONE PER ROW, AND THAT IS THE
 * ROUND-2 CORRECTION. The first cut resolved a row's two sources into a single
 * verdict with `pending` outranking `noPriorActivity`, which produced two
 * failures at once:
 *
 *   1. MASKING. A Meta verdict hid a settled — or a pending — Google one, so a
 *      row could say nothing about the source the reader was looking at.
 *   2. MIS-SCOPE. `gridCauses` then read that row-level verdict for a panel
 *      about GOOGLE MCCs, so accounts with no Google Ads at all were counted
 *      under a Google heading. Measured live 2026-09-22: the panel read 42
 *      causes + 110 uncaused, of which exactly ONE was genuinely Google.
 *
 * ⚠️ A prior-year line is withheld PER CHANNEL on the client report, so a verdict
 * is a property of (account, source) and collapsing it was the error. There is
 * now no cross-source resolution ANYWHERE, which is what makes the masking
 * unrepresentable rather than merely avoided.
 *
 * ⚠️⚠️ IT ASSERTS ONLY THE DIRECTION IT CAN PROVE, AND THAT ASYMMETRY IS THE
 * WHOLE HONESTY OF IT. `coverageFrom` later than the window's start is a
 * SUFFICIENT condition — the prior year definitely has a no-data month, so the
 * line is definitely withheld. The CONVERSE IS NOT TRUE: a covered start date
 * says nothing about an INTERIOR gap, which withholds the line just as
 * completely. So this returns a verdict or null, and null MUST NOT be read as
 * "this account has a year-on-year chart".
 *
 * ⚠️ The window comes from `yearOnYearRange` — the real function the real report
 * calls, whose own length is `YEAR_ON_YEAR_MONTHS` — rather than a restated "24
 * months". A second definition of the window is how the page comes to disagree
 * with the chart it is describing, and it is how a "25 months" figure escaped
 * into a runbook and then into a 151-account fault.
 *
 * ⚠️⚠️ META IS 172 ACTIVE ACCOUNTS AND NOBODY HAS LOOKED (measured 2026-09-22,
 * deliberately NOT built this round). Meta supplies no delivery floor, so every
 * Meta shortfall resolves `pending` — correctly, since Meta backfills PER
 * ACCOUNT and this page carries a real one-click control for it. Measured: 134
 * of 233 Meta ad accounts start after the window, reaching 172 active kyck
 * accounts whose Meta prior-year line is withheld on a client report right now.
 * ⚠️ That has never been read AS a Meta problem, because it has been rendering
 * under a GOOGLE heading. It is a real worklist with a real action; whether to
 * surface it is the next round's decision — do not quietly suppress it to make
 * a number smaller.
 *
 * ⚠️ CONSERVATIVE AT THE MONTH BOUNDARY, and knowingly. `priorYearUnavailableReason`
 * withholds on a `no-data` MONTH, while `bucketCoverage` calls a month that
 * STRADDLES the first measured day `partial` rather than `no-data` — so an
 * account starting on, say, the 14th of the earliest prior-year month still gets
 * its chart, and this says otherwise. At most one month wide, and the
 * over-report usually lands as the MUTED `noPriorActivity` rather than as a
 * worklist item. Comparing month KEYS would close it; that is a behaviour change
 * to what the page reports and belongs in its own round.
 */
export function yearOnYearForSource(
  source: MatrixSourceKey,
  cell: MatrixCell,
  coverageFrom: string | null,
  deliveryConfirmedFrom: string | null,
  nowMs: number,
): GridYearOnYear | null {
  if (!YEAR_ON_YEAR_SOURCES.includes(source)) return null;
  // ⚠️ Only where the source is actually reporting. An account with no data at
  // all has bigger problems, and its cell already says so. ⚠️⚠️ THIS IS ALSO
  // WHAT KEEPS AN ACCOUNT WITH NO GOOGLE ADS OUT OF THE GOOGLE VERDICT — the
  // gate was always here and always worked; what leaked was the ROW-LEVEL
  // collapse above it, which let a Meta verdict answer for a Google column.
  if (cell.kind !== 'health') return null;

  const today = new Date(nowMs).toISOString().slice(0, 10);
  const windowStart = yearOnYearRange(today).from;

  // ⚠️⚠️ NORMALISED TO A DATE BEFORE COMPARING, AND IT WAS AN OFF-BY-ONE (found
  // 2026-09-22). `AccountHealth.coverageFrom` is an ISO INSTANT for Google Ads
  // and a plain YYYY-MM-DD for Meta, and the comparison here is a string one —
  // so `'2024-10-01T00:00:00.000Z' <= '2024-10-01'` is FALSE, because the longer
  // string sorts after the shorter. A Google account whose history started
  // EXACTLY on the window's first day was therefore reported as short of a
  // window it fully covers.
  //
  // ⚠️ NOT HYPOTHETICAL: `2024-10-01` is a live `activity_from` value, and on
  // 2026-09-22 that was the window start to the day. The same slice is what
  // makes the sentence readable — it used to render the raw instant.
  const from = day(coverageFrom);
  if (from === null || from <= windowStart) return null;

  // ⚠️⚠️ THE WHOLE DISCRIMINATOR IS THIS ONE COMPARISON, and it is against the
  // PULL UNIT's floor rather than the account's own. Reading `from` twice here
  // would make the two verdicts the same condition — which is exactly what the
  // code did before D22e, and why it could not tell them apart.
  //
  // ⚠️ A NULL FLOOR IS UNKNOWN, AND UNKNOWN MEANS PENDING. That keeps every
  // source that has not characterised delivery on today's behaviour, instead of
  // silently reclassifying its shortfalls as settled facts nobody revisits.
  const unitFloor = day(deliveryConfirmedFrom);
  const label = MATRIX_SOURCE_LABEL[source];

  if (unitFloor !== null && unitFloor <= windowStart) {
    return {
      kind: 'noPriorActivity',
      // ⚠️ "No spend recorded" CLAIMS NEITHER CAUSE, on purpose. It is true
      // whether the account was not advertising then or was linked to the pull
      // later, and those cannot be told apart without a per-account
      // DELIVERY-start witness (see `healthQuery.ts`). Saying "did not
      // advertise" would be a guess; saying "short of the window" would send
      // someone to re-run an 18-hour job for data that may not exist.
      message: `No year-on-year comparison on the client report: no ${label} spend recorded before ${from}. The prior year has been delivered for this account's pull, so there is nothing to re-run — it was either not advertising then, or added later.`,
    };
  }
  return {
    kind: 'pending',
    message: `No year-on-year comparison on the client report: ${label} history starts ${from}, inside the twelve months it compares against. The prior year has not been delivered for this account's pull.`,
  };
}

/**
 * ⚠️⚠️ IS THIS SOURCE'S OWN PULL BEHIND? (§5, 2026-09-22)
 *
 * The page already does this correctly for Google Ads: a dead transfer collapses
 * to ONE line per cause with its blast radius stated. Meta staleness had no
 * equivalent, so when the 2-hourly pull missed two cycles every account went
 * amber independently and a single pipeline failure rendered as 234 red
 * herrings. The page became wallpaper for a morning.
 *
 * ⚠️ IT READS THE JOBS, NOT THE ACCOUNTS. That is what makes it a CAUSE rather
 * than a summary of symptoms: the question is whether the pull ran, and the
 * accounts are what the answer explains.
 *
 * ⚠️⚠️ EVERY JOB MUST BE BEHIND, NOT ANY ONE. Google Ads has eleven jobs — one
 * per MCC — and one dead transfer is a per-cause finding it already handles, not
 * a dead SOURCE. Using ANY here would suppress per-account staleness across the
 * whole of Google Ads because a single MCC was late, which is the opposite
 * mistake: hiding real findings behind a partial outage.
 *
 * ⚠️ A SOURCE WITH NO JOBS IS NOT STALE. `false` is the honest answer to a
 * question with no evidence either way, and it is also the safe one here —
 * `true` would suppress every account's staleness on no grounds at all.
 */
export function isSourceStale(
  source: SourceHealth,
  nowMs: number,
): boolean {
  if (source.jobs.length === 0) return false;
  return source.jobs.every((job) => jobStatus(job, source.expectation, nowMs).status !== 'green');
}

/**
 * ⚠️⚠️ ONE FINDING PER STALE SOURCE, WITH ITS BLAST RADIUS — the Google causes
 * panel's shape, applied to the source itself.
 *
 * ⚠️ `accountsAffected` IS THE NUMBER THE READER NEEDS and it is what the 234
 * rows were trying to say. Without it the banner is a bare statement that
 * something is wrong; with it, it is the same information in one line.
 */
export interface StaleSourceFinding {
  source: HealthSourceKey;
  /** What the page calls the job. Falls back to the source key. */
  jobLabel: string;
  /** What is wrong, in one sentence, from `jobStatus`. */
  reason: string;
  /** When that job last ran, or null if it never has. */
  lastRunAt: string | null;
  /**
   * ⚠️ How many accounts' staleness this explains — the rows it SUPPRESSED.
   * Derived from the grid's own lines, so it can never claim to have suppressed
   * something the grid did not.
   */
  accountsAffected: number;
}

export interface GridInput {
  accounts: readonly GridAccount[];
  sources: readonly SourceHealth[];
  /** Sources whose read FAILED. ⚠️ Named, never dropped — see `buildGrid`. */
  failedSources: readonly HealthSourceKey[];
  /**
   * ⚠️⚠️ DEMOGRAPHICS TRAVELS AS ITS OWN `SourceHealth`, NOT AS A MAP OF
   * PRE-BAKED VERDICTS. The first draft passed a map and the page filled it with
   * a hardcoded green for every account that had a row — so the column would
   * have reported PERFECT HEALTH for a pull that currently has no scheduler at
   * all. A verdict computed anywhere but `accountStatus` is a second opinion
   * about staleness, and this one was a flattering constant.
   *
   * ⚠️ It cannot simply join `sources`: its `expectation.source` is `'meta'`
   * (there is no separate key in `HealthSourceKey`), so it would collide with
   * Meta's own entry and one of the two would silently win.
   */
  demographics: SourceHealth | null;
  nowMs: number;
}

export function buildGrid(input: GridInput): GridRow[] {
  const { accounts, sources, failedSources, demographics, nowMs } = input;

  const bySource = new Map<HealthSourceKey, SourceHealth>();
  for (const s of sources) bySource.set(s.expectation.source, s);

  // accountId -> AccountHealth, per source.
  const healthBy = new Map<HealthSourceKey, Map<string, AccountHealth>>();
  for (const s of sources) {
    const m = new Map<string, AccountHealth>();
    for (const a of s.accounts) m.set(a.accountId, a);
    healthBy.set(s.expectation.source, m);
  }

  const demoHealth = new Map<string, AccountHealth>();
  for (const a of demographics?.accounts ?? []) demoHealth.set(a.accountId, a);

  const failed = new Set(failedSources);

  // ⚠️⚠️ COMPUTED ONCE PER SOURCE, NOT PER ACCOUNT (§5). It is a property of the
  // pull, so asking it 234 times would be 234 chances for the answer to differ —
  // and the whole point is that every account under a stale source shares ONE
  // cause. Demographics resolves against its OWN source, since it is a separate
  // pull on a separate schedule and a healthy campaign pull must not vouch for it.
  const staleBySource = new Map<HealthSourceKey, boolean>();
  for (const s of sources) staleBySource.set(s.expectation.source, isSourceStale(s, nowMs));
  const demoStale = demographics === null ? false : isSourceStale(demographics, nowMs);

  const rows: GridRow[] = [];

  for (const account of accounts) {
    if (!isGridRow(account)) continue;

    const cells = {} as Record<MatrixSourceKey, MatrixCell>;
    const detail: GridRowDetailLine[] = [];
    let worst: HealthStatus | null = null;
    let hasDrift = false;
    const liveSources: MatrixSourceKey[] = [];
    const missingSources: MatrixSourceKey[] = [];

    for (const source of MATRIX_SOURCES) {
      const healthKey = HEALTH_SOURCE_FOR_COLUMN[source];
      const src = bySource.get(healthKey);
      const health = healthBy.get(healthKey)?.get(account.accountId) ?? null;

      // ⚠️ Demographics resolves against its OWN health and its OWN expectation
      // (nightly, not 2-hourly) — through the SAME `accountStatus` as every
      // other column, so there is one definition of stale.
      const isDemo = source === 'metaDemographics';
      const resolved = isDemo
        ? demographics === null
          ? null
          : {
              health: demoHealth.get(account.accountId) ?? null,
              expectation: demographics.expectation,
            }
        : src === undefined
          ? null
          : { health, expectation: src.expectation };

      // ⚠️ THE DEMOGRAPHICS COLUMN TAKES ITS OWN SOURCE'S STALENESS, for the same
      // reason it takes its own expectation: the nightly audience pass and the
      // 2-hourly campaign pull fail independently.
      const sourceStale = isDemo ? demoStale : (staleBySource.get(healthKey) ?? false);
      const verdict =
        resolved !== null && resolved.health !== null
          ? accountStatus(resolved.health, resolved.expectation, nowMs, sourceStale)
          : null;
      // ⚠️ WHAT THE SUPPRESSION ACTUALLY HID. Re-asking the same question with the
      // flag OFF is the only honest way to count it: anything else would be a
      // second opinion about staleness living beside `accountStatus`.
      const stalenessSuppressed =
        sourceStale &&
        resolved !== null &&
        resolved.health !== null &&
        accountStatus(resolved.health, resolved.expectation, nowMs, false).status !== 'green' &&
        verdict?.status === 'green';

      // ⚠️ A source we could not READ is `unknown` for every account, whether or
      // not we happen to hold a stale verdict for one.
      const sourceFailed = isDemo
        ? demographics === null || failed.has('meta')
        : failed.has(healthKey) || src === undefined;

      const cell = matrixCell({
        source,
        config: account,
        sourceFailed,
        inRoster: account.inRoster[source] === true,
        verdict,
        // ⚠️ META COLUMNS ONLY. Drift is a Meta ad-account concept; there is no
        // Google or CRM equivalent, and painting one on those columns would
        // assert a fault in a pipeline the classification says nothing about.
        drift: source === 'meta' || source === 'metaDemographics' ? account.drift : null,
      });
      cells[source] = cell;

      const status = cellStatus(cell);
      if (status !== null && (worst === null || STATUS_RANK[status] < STATUS_RANK[worst])) {
        worst = status;
      }
      if (cell.kind === 'stillSyncing') liveSources.push(source);
      if (cell.kind === 'missing') missingSources.push(source);
      if (cell.kind === 'drift') hasDrift = true;

      const { action, href } = actionFor(source, cell);
      // ⚠️ The DEMOGRAPHICS row's dates, never Meta's — they are different
      // measurements, and showing the campaign tier's dates under an audience
      // verdict would let a healthy pull vouch for one that never ran.
      const shown = isDemo ? (demoHealth.get(account.accountId) ?? null) : health;
      detail.push({
        source,
        cell,
        dataThrough: shown?.dataThrough ?? null,
        coverageFrom: shown?.coverageFrom ?? null,
        deliveryConfirmedThrough: shown?.deliveryConfirmedThrough ?? null,
        deliveryConfirmedFrom: shown?.deliveryConfirmedFrom ?? null,
        stalenessSuppressed,
        yearOnYear: yearOnYearForSource(
          source,
          cell,
          shown?.coverageFrom ?? null,
          shown?.deliveryConfirmedFrom ?? null,
          nowMs,
        ),
        sharedWith: shown?.sharedWith ?? [],
        action,
        href,
      });
    }

    rows.push({
      account,
      cells,
      rowStatus: worst,
      hasDrift,
      liveSources,
      missingSources,
      detail,
    });
  }

  return rows;
}

/**
 * ⚠️⚠️ CANCELLED ROWS NEVER COMPETE WITH ACTIVE PROBLEMS FOR THE TOP (D20b#4),
 * and that is enforced by the FIRST sort key rather than by a filter someone has
 * to remember to apply. A decommission backlog is not an outage.
 *
 * Within active rows: red, then amber, then green, then rows with no colour at
 * all (all "–" or all discrepancy) — ⚠️ with DISCREPANCIES ranked ABOVE plain
 * green, because "signed up and never wired" is a finding and a working account
 * is not.
 *
 * ⚠️⚠️ Within CANCELLED rows: MOST LIVE SOURCES FIRST. That is the list Scott
 * asked for in as many words — "cancelled accounts with the most green cells" —
 * and sorting them by staleness instead would sort them by exactly the wrong
 * key, because staleness on a cancelled account is the EXPECTED outcome.
 */
export function gridSortKey(row: GridRow): [number, number, string] {
  const cancelled = isCancelled(row.account) ? 1 : 0;
  if (cancelled === 1) {
    // Negative so a DESCENDING count sorts ascending alongside the other keys.
    return [1, -row.liveSources.length, row.account.accountName.toLowerCase()];
  }
  const rank =
    row.rowStatus !== null
      ? STATUS_RANK[row.rowStatus]
      : row.missingSources.length > 0
        ? 1.5 // between amber (1) and green (2)
        : 3;
  return [0, rank, row.account.accountName.toLowerCase()];
}

export function sortGridRows(rows: readonly GridRow[]): GridRow[] {
  return [...rows].sort((a, b) => {
    const ka = gridSortKey(a);
    const kb = gridSortKey(b);
    if (ka[0] !== kb[0]) return ka[0] - kb[0];
    if (ka[1] !== kb[1]) return ka[1] - kb[1];
    return ka[2] < kb[2] ? -1 : ka[2] > kb[2] ? 1 : 0;
  });
}

/**
 * The filter presets. ⚠️ `cancelledStillSyncing` IS THE DECOMMISSION WORKLIST
 * (D20b#2) — the answer to the 122, and the reason cancelled rows are shown at
 * all rather than filtered away.
 */
export type GridPreset =
  | 'all'
  | 'needsAttention'
  | 'adAccountDrift'
  | 'cancelledStillSyncing'
  | 'signedUpNeverWired';

export interface GridFilters {
  preset: GridPreset;
  /** Free text over account name. */
  search: string;
  /** Empty = no constraint. */
  accountTypes: readonly string[];
  accountStatuses: readonly string[];
  brands: readonly string[];
  groups: readonly string[];
  accountManagers: readonly string[];
  /** Keep a row only if one of these sources carries a non-green finding. */
  sources: readonly MatrixSourceKey[];
}

export const EMPTY_FILTERS: GridFilters = {
  preset: 'all',
  search: '',
  accountTypes: [],
  accountStatuses: [],
  brands: [],
  groups: [],
  accountManagers: [],
  sources: [],
};

const rowHasFindingIn = (row: GridRow, source: MatrixSourceKey): boolean => {
  const cell = row.cells[source];
  if (cell.kind === 'health') return cell.status !== 'green';
  return (
    cell.kind === 'missing' ||
    cell.kind === 'stillSyncing' ||
    cell.kind === 'unknown' ||
    cell.kind === 'drift'
  );
};

// ⚠️ An EMPTY list is NO CONSTRAINT; a list with entries narrows. An account
// whose brand/group/manager is unresolved (null) can never match a named one,
// so it drops out of a narrowed view rather than matching everything — the
// empty-vs-absent rule at the filter layer.
const matches = (selected: readonly string[], value: string | null): boolean =>
  selected.length === 0 || (value !== null && selected.includes(value));

export function filterGridRows(rows: readonly GridRow[], f: GridFilters): GridRow[] {
  const needle = f.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (needle !== '' && !row.account.accountName.toLowerCase().includes(needle)) return false;
    if (f.accountTypes.length > 0 && !f.accountTypes.includes(row.account.accountType)) return false;
    if (f.accountStatuses.length > 0 && !f.accountStatuses.includes(row.account.accountStatus)) {
      return false;
    }
    if (!matches(f.brands, row.account.brandName)) return false;
    if (!matches(f.groups, row.account.groupName)) return false;
    if (!matches(f.accountManagers, row.account.accountManagerName)) return false;
    if (f.sources.length > 0 && !f.sources.some((s) => rowHasFindingIn(row, s))) return false;

    switch (f.preset) {
      case 'needsAttention':
        // ⚠️ ACTIVE rows only. A cancelled account's staleness is not attention,
        // and letting it in here is how the decommission backlog drowns the
        // outages this preset exists to surface.
        return !isCancelled(row.account) && (row.rowStatus === 'red' || row.rowStatus === 'amber');
      case 'cancelledStillSyncing':
        return isCancelled(row.account) && row.liveSources.length > 0;
      case 'adAccountDrift':
        return row.hasDrift;
      case 'signedUpNeverWired':
        return row.missingSources.length > 0;
      case 'all':
        return true;
    }
  });
}

/**
 * ⚠️⚠️ THE GOOGLE ADS CAUSE SCOPE — STATED, WITH NO BUTTON (D22c/D22d).
 *
 * There is NO per-customer backfill in BigQuery Data Transfer. The unit is the
 * TRANSFER CONFIG, one per MCC, and re-running a historical range re-fetches
 * EVERY customer under it — measured 2026-09-02 at ~18 hours wall clock, set by
 * the longest chunk and so not shortenable by splitting.
 *
 * ⚠️ So a "backfill" control on a Google Ads CELL would fire an MCC-wide job
 * affecting N sibling accounts the row does not name. That is §12's action
 * invariant broken in a new way: the control EXISTS, but its BLAST RADIUS does
 * not match the row it sits on, and an action that silently does more than its
 * row implies is worse than an absent one.
 *
 * ⚠️ THE ACCOUNT ROW THEREFORE STATES THE SHORTFALL AND NAMES ITS CAUSE; THIS
 * gives the cause its SCOPE — how many accounts one such run would rewrite —
 * with NO action attached. Operator design for the run itself is separate.
 */
export interface GridCause {
  causeKey: string;
  causeLabel: string;
  /** ⚠️ Every account under this cause, not just the ones with a finding. */
  accountCount: number;
  accountNames: string[];
  /** ⚠️ How many of them are waiting on PENDING prior-year data. See below. */
  shortfallCount: number;
}

/**
 * ⚠️⚠️ THE SUM MUST RECONCILE WITH AN INDEPENDENT WITNESS, WHICH IS WHY THIS IS
 * NOT JUST AN ARRAY (D22e).
 *
 *     Σ causes[].shortfallCount + uncaused === Google accounts whose own coverage
 *                                              starts after the window AND whose
 *                                              MCC floor does not reach it
 *
 * ⚠️⚠️ THE WITNESS IS COUNTED FROM THE `SourceHealth` RECORDS, NOT FROM THESE
 * ROWS, and that distinction is the whole value of the assertion. Round 1
 * reconciled against `shortfallIds.size` — but `uncaused` is DEFINED as
 * `shortfallIds.size - claimed`, so the check reduced to `x - y + y === x`. It
 * was arithmetic: true for every input, written in the same commit as the
 * definition, and it passed over the live defect it was meant to catch.
 * Demonstrated by re-introducing that defect: the old form still passed.
 * See `gridYearOnYear.test.ts` and CLAUDE.md's rule on witnesses.
 *
 * The old shape could not state that. It built its shortfall set from every
 * withheld row, then skipped any account with no `causeKey` and finally dropped
 * causes with a zero count — so an account that WAS short but carried no cause
 * was counted NOWHERE, and the panel looked complete while understating itself.
 * ⚠️ Measured on live data 2026-09-22 before this change: 58 withheld rows
 * against Σ 55. Three accounts (Anytime Fitness Ormiston, Snap Fitness Altona
 * North, Snap Fitness Port Melbourne) were invisible on a panel whose entire job
 * is to be the count. `locationTree.ts` names that failure as "a silent
 * shortfall, and the single most misleading thing this page could do".
 *
 * ⚠️ WHY AN ACCOUNT HAS NO CAUSE, since the answer is not obvious: `causeKey` is
 * set only when the account's customer is IN STEP with its MCC's delivery. A
 * STRAGGLER — current MCC, one customer behind — deliberately keeps its own row
 * (D8) and therefore has no cause key. All three of the accounts above were
 * stragglers.
 *
 * ⚠️⚠️ SO `causeKey` IS A STALENESS-GROUPING KEY BEING READ AS A BACKFILL UNIT,
 * and they are not the same question: a straggler still belongs to an MCC for
 * backfill purposes. Keying this panel on the account's MCC directly would fix
 * that at the root AND stop `accountCount` understating the blast radius (today
 * it excludes stragglers from the very number a reader uses to decide whether to
 * fire an 18-hour job). That needs the MCC on the account health as its own
 * field, and it is DELIBERATELY NOT DONE HERE — flagged rather than taken,
 * because it is wider than the defect. The remainder below makes the current
 * shape honest in the meantime.
 */
export interface GridCauseSummary {
  causes: GridCause[];
  /**
   * ⚠️ GOOGLE accounts waiting on prior-year data that no cause claims. Rendered,
   * never dropped — a remainder the panel does not mention is the silent
   * shortfall this shape exists to prevent.
   */
  uncaused: number;
}

/**
 * ⚠️⚠️ THE GOOGLE VERDICT ON A ROW, NAMED. Every reader of a prior-year verdict
 * has to say WHICH SOURCE it wants, and this is what that looks like for the
 * Google causes panel. A row-level verdict let a Meta shortfall answer here —
 * measured live 2026-09-22, 110 accounts with no Google Ads at all were counted
 * under a heading that says "Google Ads".
 */
export function googleYearOnYear(row: GridRow): GridYearOnYear | null {
  return row.detail.find((d) => d.source === 'googleAds')?.yearOnYear ?? null;
}

export function gridCauses(
  rows: readonly GridRow[],
  sources: readonly SourceHealth[],
): GridCauseSummary {
  const google = sources.find((s) => s.expectation.source === 'googleAds');
  if (google === undefined) return { causes: [], uncaused: 0 };

  const byKey = new Map<string, GridCause>();
  // ⚠️⚠️ THE GOOGLE VERDICT, `pending` ONLY. Two narrowings, and BOTH were live
  // defects rather than tidiness (measured 2026-09-22, panel showed 42 + 110):
  //
  //   GOOGLE   a Meta shortfall is not a Google transfer-config problem. 110 of
  //            the 152 had no Google Ads connection at all.
  //   pending  an account with no prior-year SPEND is not short of anything we
  //            can fetch. That was 57 accounts on a worklist with no action.
  //
  // ⚠️ Exactly ONE account survived both: Jetts Lutwyche, whose MCC genuinely
  // floors at 2026-09-02.
  const shortfallIds = new Set(
    rows
      .filter((r) => googleYearOnYear(r)?.kind === 'pending')
      .map((r) => r.account.accountId),
  );
  // ⚠️ Counted over the SOURCE's own accounts rather than the grid's visible
  // rows: the blast radius is every customer under the MCC, whether or not a
  // filter is hiding it. A scope that shrank with the filter would understate
  // exactly the number the reader needs.
  for (const a of google.accounts) {
    if (a.causeKey === null || a.causeLabel === null) continue;
    const hit = byKey.get(a.causeKey) ?? {
      causeKey: a.causeKey,
      causeLabel: a.causeLabel,
      accountCount: 0,
      accountNames: [],
      shortfallCount: 0,
    };
    hit.accountCount += 1;
    hit.accountNames.push(a.accountName);
    if (shortfallIds.has(a.accountId)) hit.shortfallCount += 1;
    byKey.set(a.causeKey, hit);
  }
  const causes = [...byKey.values()]
    .filter((c) => c.shortfallCount > 0)
    .sort((a, b) => b.shortfallCount - a.shortfallCount);

  // ⚠️ THE REMAINDER, COMPUTED FROM THE ROWS RATHER THAN FROM THE SOURCE. An
  // account can be short and absent from `google.accounts` entirely, and either
  // way it is a row the reader can see — so the total this reconciles against is
  // the one on screen.
  const claimed = causes.reduce((n, c) => n + c.shortfallCount, 0);
  return { causes, uncaused: Math.max(0, shortfallIds.size - claimed) };
}

/**
 * ⚠️⚠️ THE STALE-SOURCE FINDINGS — one line per source whose pull is behind,
 * with the account rows it accounted for (§5, 2026-09-22).
 *
 * ⚠️ `accountsAffected` IS COUNTED FROM THE ROWS, never asserted: it is exactly
 * the lines whose staleness `accountStatus` withheld, so the banner cannot claim
 * to have absorbed a finding the grid still shows. Measured 2026-09-22 the
 * number this replaces was 234.
 *
 * ⚠️ A STALE SOURCE WITH ZERO SUPPRESSED ROWS IS STILL REPORTED. The pull being
 * behind is the finding; that no account had gone stale YET is a fact about
 * timing, and staying silent until the damage shows is the opposite of what a
 * cause row is for.
 */
export function staleSourceFindings(
  rows: readonly GridRow[],
  sources: readonly SourceHealth[],
  nowMs: number,
): StaleSourceFinding[] {
  const out: StaleSourceFinding[] = [];
  for (const src of sources) {
    if (!isSourceStale(src, nowMs)) continue;
    // ⚠️ The WORST job's sentence, so a source with several behind jobs reports
    // the one a reader should act on rather than whichever came first.
    const verdicts = src.jobs
      .map((job) => ({ job, v: jobStatus(job, src.expectation, nowMs) }))
      .sort((a, b) => STATUS_RANK[a.v.status] - STATUS_RANK[b.v.status]);
    const worst = verdicts[0];
    const columns = MATRIX_SOURCES.filter(
      (col) => HEALTH_SOURCE_FOR_COLUMN[col] === src.expectation.source,
    );
    let affected = 0;
    for (const row of rows) {
      // ⚠️ ONE PER ROW, not one per column: Meta owns two columns, and an account
      // suppressed on both is still ONE account the reader would have seen.
      if (row.detail.some((d) => columns.includes(d.source) && d.stalenessSuppressed)) {
        affected += 1;
      }
    }
    out.push({
      source: src.expectation.source,
      jobLabel: worst?.job.jobLabel ?? src.expectation.source,
      reason: worst?.v.reason ?? 'This source has not completed a run recently.',
      lastRunAt: worst?.job.lastRunAt ?? null,
      accountsAffected: affected,
    });
  }
  return out;
}

export interface GridSummary {
  totalRows: number;
  /** ⚠️ ACTIVE accounts with a red or amber cell. Cancelled never counts here. */
  needsAttention: number;
  cancelledStillSyncing: number;
  /** ⚠️ ACTIVE accounts bound to the wrong ad account. A config fault. */
  adAccountDrift: number;
  /**
   * ⚠️⚠️ THE DISCREPANCY COUNT IS MEASURED HERE AND HAS NEVER BEEN MEASURED
   * ANYWHERE ELSE — which is why `missing` still has no colour.
   *
   * D18b reported 154 ("signed up, never wired"). D19b corrected it to ~60 by
   * adding 84 shared-covered accounts to 328 own links. ⚠️ THAT CORRECTION IS
   * ALSO WRONG, and in the direction that understates the finding:
   * `accountSelection.classify` pushes an account to `targets` only AFTER
   * requiring a non-empty `fbAdAccountId`, and `dedupeSharedAdAccounts` demotes
   * to `covered-by-shared-pull` only from `targets`. So every shared-covered
   * account HAS a link of its own — it shares the VALUE — and the 84 are a
   * SUBSET of the 328, not an addition to them. (The brief's own figures agree:
   * 472 - 154 = 318 with both, plus 10 wired-but-not-signed-up = 328.)
   *
   * ⚠️ So neither 154 nor ~60 is quoted anywhere, and this field is the first
   * honest count. Colour `missing` once a live run reports it.
   */
  signedUpNeverWired: number;
  /** Sources we could not read at all. ⚠️ Named on the page, never dropped. */
  failedSources: HealthSourceKey[];
  /**
   * ⚠️⚠️ TRUE ONLY WHEN WE ACTUALLY CHECKED AND FOUND NOTHING. An EMPTY GRID IS
   * NEVER ALL CLEAR — no rows means either nothing loaded or nothing matched,
   * and both read as good news on a page whose job is bad news. A failed source
   * is not all-clear either, and neither is a source that returned no accounts:
   * every source reporting zero is a FAILURE state, not a clean one.
   */
  allClear: boolean;
}

export function summariseGrid(
  rows: readonly GridRow[],
  failedSources: readonly HealthSourceKey[],
  sources: readonly SourceHealth[],
): GridSummary {
  let needsAttention = 0;
  let cancelledStillSyncing = 0;
  let signedUpNeverWired = 0;
  let adAccountDrift = 0;
  for (const row of rows) {
    if (isCancelled(row.account)) {
      if (row.liveSources.length > 0) cancelledStillSyncing += 1;
    } else if (row.rowStatus === 'red' || row.rowStatus === 'amber') {
      needsAttention += 1;
    }
    if (row.missingSources.length > 0) signedUpNeverWired += 1;
    if (row.hasDrift) adAccountDrift += 1;
  }

  const everySourceEmpty = sources.length === 0 || sources.every((s) => s.accounts.length === 0);
  const allClear =
    rows.length > 0 &&
    failedSources.length === 0 &&
    !everySourceEmpty &&
    needsAttention === 0 &&
    cancelledStillSyncing === 0 &&
    signedUpNeverWired === 0 &&
    adAccountDrift === 0;

  return {
    totalRows: rows.length,
    needsAttention,
    cancelledStillSyncing,
    adAccountDrift,
    signedUpNeverWired,
    failedSources: [...failedSources],
    allClear,
  };
}

/** Every service a column depends on — for the filter bar's account-type options. */
export const COLUMN_SERVICES: readonly string[] = Array.from(
  new Set(Object.values(SERVICE_FOR_SOURCE).filter((s): s is string => s !== null)),
);
